import BaseService from '@/api/services'
import Account from '@/api/models/Account'
import { StatusesMap } from '@/models/StatusesMap'
import { BaseResponse } from '@/api/models/BaseResponse'
import AccountOption from '@/api/models/AccountOption'
import AccountOptions from '@/models/AccountOptions'
import SystemSettings from '@/models/SystemSettings'
import { PaymentType } from '@/api/models/PaymentType'
import { ExtraFields } from '@/models/ExtraFields'
import StoreAddress from '@/api/models/store/StoreAddress'
import Store from '@/api/models/store/Store'
import StoreMap from '@/api/models/store/StoreMap'
import UpdateStoreMapRequest from '@/api/models/request/UpdateStoreMapRequest'
import CdekPickupPoint from '@/models/CdekPickupPoint'
import DeliveryRule from '@/api/models/DeliveryRule'
import SaveDeliveryRuleRequest from '@/api/models/request/SaveDeliveryRuleRequest';

export default class AccountService extends BaseService {
    public async updateAccount (account: Account): Promise<Account> {
        const result = await this.api.patch<unknown, BaseResponse<Account>>(
            this.getUrl('update_account', { accountId: account.id }),
            account
        )

        return result.data.data
    }

    public async createAccount (account: Account): Promise<Account> {
        const result = await this.api.post<unknown, BaseResponse<Account>>(this.getUrl('add_account'), account)

        return result.data.data
    }

    public async updateStatuses(accountId: number, statusesMap: StatusesMap): Promise<void> {
        await this.api.post<unknown, BaseResponse<boolean>>(
            this.getUrl('save_account_statuses_mappings', { accountId: accountId }),
            statusesMap
        )
    }

    public async getAccountOptions(accountId: number): Promise<AccountOptions> {
        const result = await this.api.get<unknown, BaseResponse<AccountOptions>>(
            this.getUrl('get_internal_options', { accountId: accountId })
        )

        return result.data.data
    }

    public async getAccountSystemOptions(accountId: number): Promise<SystemSettings> {
        const result = await this.api.get<unknown, BaseResponse<SystemSettings>>(
            this.getUrl('get_system_options', { accountId: accountId })
        )

        return result.data.data
    }

    public async updateAccountOptions(accountId: number, accountOption: AccountOption): Promise<AccountOption> {
        const result = await this.api.post<unknown, BaseResponse<AccountOption>>(
            this.getUrl('save_account_option', { accountId: accountId }),
            accountOption
        )

        return result.data.data
    }

    public async getPaymentTypes(accountId: number): Promise<Array<PaymentType>> {
        const result = await this.api.get<unknown, BaseResponse<Array<PaymentType>>>(
            this.getUrl('get_account_payment_types', { accountId: accountId })
        )

        return result.data.data
    }

    public async updatePaymentTypes(accountId: number, paymentType: PaymentType): Promise<PaymentType> {
        const result = await this.api.post<unknown, BaseResponse<PaymentType>>(
            this.getUrl('save_account_payment_type', { accountId: accountId }),
            paymentType
        )

        return result.data.data
    }

    public async getExtraFields(accountId: number): Promise<ExtraFields> {
        const result = await this.api.get<unknown, BaseResponse<ExtraFields>>(
            this.getUrl('get_extrafields_options', { accountId: accountId })
        )

        return result.data.data
    }

    public async getStatusesMappings(accountId: number): Promise<Array<StatusesMap>> {
        const result = await this.api.get<unknown, BaseResponse<Array<StatusesMap>>>(
            this.getUrl('get_account_statuses_mappings', { accountId: accountId })
        )

        return result.data.data
    }

    public async getCdekPickupPoints(accountId: number, storeCode: string, address: StoreAddress): Promise<Array<CdekPickupPoint>> {
        const response = await this.api.get<unknown, Array<CdekPickupPoint>>(
            this.getUrl(
                'get_cdek_pickup_points',
                { accountId: accountId, storeCode: storeCode, ...address }
            )
        )
        return response.data.data
    }

    public async getWarehousesMappings(accountId: number): Promise<Array<StoreMap>> {
        const response = await this.api.get<unknown, Array<Store>>(
            this.getUrl(
                'get_warehouses_mappings',
                { accountId: accountId }
            )
        )
        return response.data.data
    }

    public async updateWarehouseMapping(request: UpdateStoreMapRequest): Promise<void> {
        await this.api.post<unknown, BaseResponse<StoreMap>>(
            this.getUrl('save_warehouses_mappings', { accountId: request.accountId }),
            { map: request.map, needUpdateModule: request.needUpdateModule }
        )
    }

    public async getCdekPickupPointByCode(code: string, accountId: number, address: StoreAddress, cityCode: number|undefined): Promise<CdekPickupPoint> {
        const response = await this.api.get<unknown, Array<CdekPickupPoint>>(
            this.getUrl(
                'get_cdek_pickup_point',
                { accountId, ...address, pointCode: code, cityCode: cityCode }
            )
        )
        return response.data.data
    }

    public async getAvailableTariffs(accountId: number): Promise<Array<number>> {
        const response = await this.api.get<unknown, Array<number>>(this.getUrl('get_available_tariffs', { accountId }))
        return response.data.data
    }

    public async updateAvailableTariffs(accountId: number, tariffs: Array<number>): Promise<Array<number>> {
        const response = await this.api.post<unknown, Array<number>>(
            this.getUrl('save_available_tariffs', { accountId }),
            { availableTariffs: tariffs }
        )
        return response.data.data
    }

    public async getDeliveryRules(accountId: number): Promise<Array<DeliveryRule>> {
        const response = await this.api.get<unknown, Array<number>>(this.getUrl('get_delivery_rules', { accountId }))
        return response.data.data;
    }

    public async saveDeliveryRules(request: SaveDeliveryRuleRequest) {
        const response = await this.api.post<unknown, Array<number>>(
            this.getUrl('save_delivery_rules', { accountId: request.accountId }),
            { id: request.id, threshold: request.threshold, value: request.value }
        )

        return response.data.data
    }

    public async deleteDeliveryRule(accountId: number, deliveryRuleId: number): Promise<void> {
        await this.api.delete<unknown, Array<number>>(
            this.getUrl('delete_delivery_rule', { accountId, deliveryRuleId })
        )
    }
}
